import { DashboardState, ESdChangeReason, ESymbolCategory, type IDisplayItem } from "@kuika/kuika-cl-model";
import { Cascader } from "antd/es";
import { CascaderOptionType, CascaderValueType } from "antd/es/cascader";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React, { CSSProperties } from "react";
import ReactDOM from "react-dom";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { countCascaderSearchResults, ICascaderSearchResultCount } from "../../../../shared/utility/cascader-search";
import {
  DesignerSymbolPickerEngine,
  ISymbolPickerCascaderProps
} from "../../../../shared/utility/designer-symbol-picker-engine";
import {
  compareSymbolPickerPaths,
  matchSymbolPickerPath,
  renderSymbolPickerSearchRow,
  SEARCH_MATCH_LIMIT
} from "../../../../shared/utility/symbol-picker-search";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import {
  attachPickerPopupBehaviours,
  ensurePickerPopupChrome,
  IPickerPopupBehaviours,
  mirrorSearchToInput,
  PICKER_EXPAND_ICON,
  PickerPopupEmptyState,
  PickerPopupHeader
} from "./picker-popup-chrome";
import { clearPickerCursor, handlePickerNavigationKey, isPickerNavigationKey } from "./picker-popup-keyboard";
import { decorateSymbolPickerOptions } from "./symbol-picker-option-testids";
import { isSymbolPickerColumnsV2Enabled } from "./symbol-picker-popup/symbol-picker-columns-flag";
import { buildSymbolPickerPreviewContext } from "./symbol-picker-popup/symbol-picker-host-context";
import { SymbolPickerPopup } from "./symbol-picker-popup/symbol-picker-popup";
import type { ISymbolPickerPreviewContext } from "./symbol-picker-popup/symbol-picker-preview-model";

/** Cache key for the header's result count — see getSearchResultCount. */
interface ISymbolSearchResultCountMemo {
  searchText: string;
  options: CascaderOptionType[];
  count: ICascaderSearchResultCount;
}

/** Cache key for the test-id-decorated option tree — see getDecoratedOptions. */
interface IDecoratedOptionsMemo {
  source: CascaderOptionType[];
  decorated: CascaderOptionType[];
}

export interface ISymbolPickerCascaderState {
  options: CascaderOptionType[];
  description: string;
  uniqueKey: Guid;
  popupVisible: boolean;
  popupChromeReady: boolean;
  popupSearchText: string;
}

class SymbolPickerCascader extends PureSdComponent<
  ISymbolPickerCascaderProps & IWithSDProps,
  ISymbolPickerCascaderState
> {
  constructor(props: ISymbolPickerCascaderProps & IWithSDProps) {
    super(props);
    this.state = {
      options: [],
      description: "Fixed Value",
      uniqueKey: Guid.create(),
      popupVisible: false,
      popupChromeReady: false,
      popupSearchText: ""
    };
    this.isColumnsV2 = isSymbolPickerColumnsV2Enabled();
  }

  /**
   * `symbolPickerColumnsV2`: the popup is the columns popup (symbol-picker-popup/) instead of antd's
   * Cascader. Read once per mount — flipping the flag takes effect as pickers re-mount (a reload).
   */
  readonly isColumnsV2: boolean;

  _popupHeaderEl: HTMLElement | null = null;

  _popupBehaviours: IPickerPopupBehaviours | null = null;

  getEngine = () => new DesignerSymbolPickerEngine(this.props);

  getPopupElement = (): HTMLElement | null => {
    const popupUniqueClassName = `kcascader_${this.state.uniqueKey.toString().substring(0, 8)}`;
    return (document.getElementsByClassName(popupUniqueClassName)[0] as HTMLElement | undefined) ?? null;
  };

  /** The picker's own root div — every trigger-side DOM lookup is scoped to it, not to `document`. */
  _rootEl: HTMLDivElement | null = null;

  saveRootElement = (element: HTMLDivElement | null): void => {
    this._rootEl = element;
  };

  getSearchBoxElement = (): HTMLElement | null => {
    return this._rootEl ? this._rootEl.querySelector<HTMLElement>(".ant-cascader-input") : null;
  };

  // Popup chrome (picker-popup-chrome.tsx): a header holding the visible search field, rendered
  // into the popup through a portal.
  setupPopupChrome = (): void => {
    const popup = this.getPopupElement();
    if (!popup) return;

    const chrome = ensurePickerPopupChrome(popup);
    this._popupHeaderEl = chrome.header;

    if (!this._popupBehaviours) {
      this._popupBehaviours = attachPickerPopupBehaviours(popup, { restoreFocus: this.focusPopupSearchInput });
    }

    if (!this.state.popupChromeReady) {
      this.setState({ popupChromeReady: true });
    }
  };

  handlePopupSearchChange = (text: string): void => {
    // The row list is about to be replaced, so a keyboard cursor on the old one means nothing.
    const popup: HTMLElement | null = this.getPopupElement();
    if (popup) clearPickerCursor(popup);
    // The --searching popup class is derived from this state in render() (popupClassName).
    this.setState({ popupSearchText: text });
    mirrorSearchToInput(this.getSearchBoxElement(), text);
  };

  /**
   * The popup's keyboard: arrows move a cursor through the columns (or the flat result list while
   * searching), Enter picks through the row's own click handler, Escape closes. All of it lives in
   * picker-popup-keyboard.ts; this only supplies the popup and the picker's hooks.
   *
   * Escape stops propagation: the picker often sits inside a KModal (the Add Condition dialog), and
   * this header is portaled from inside that modal's React tree, so the key would otherwise reach
   * rc-dialog's handler and close the dialog along with the popup.
   */
  handlePopupSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      this.getOptions(false);
      return;
    }
    if (!isPickerNavigationKey(event.key)) return;
    const popup: HTMLElement | null = this.getPopupElement();
    if (!popup) return;
    const handled: boolean = handlePickerNavigationKey(popup, event.key, {
      isSearching: this.state.popupSearchText !== "",
      restoreFocus: this.focusPopupSearchInput,
      afterCommit: (work: () => void): void => {
        requestAnimationFrame(work);
      }
    });
    if (handled) {
      event.preventDefault();
    }
  };

  clearPopupSearch = (): void => {
    if (this._popupBehaviours) {
      this._popupBehaviours.disconnect();
      this._popupBehaviours = null;
    }
    if (this.state.popupSearchText !== "") {
      this.setState({ popupSearchText: "" });
      mirrorSearchToInput(this.getSearchBoxElement(), "");
    }
  };

  focusPopupSearchInput = (): void => {
    // Also reached a frame after a pointer click in the popup; if that click was a pick the popup
    // is closing, and focusing its field would leave focus on <body> once it hides.
    if (!this.state.popupVisible) return;
    const popup = this.getPopupElement();
    const input: HTMLInputElement | null = popup
      ? popup.querySelector(".kuika-action-picker-popup-search__input")
      : null;
    if (input) input.focus();
  };

  handleKeyDown = (): void => {
    if (this.state.popupVisible) {
      this.focusPopupSearchInput();
    }
  };

  getOptions = (popupVisible: boolean): void => {
    const searchBox: HTMLElement | null = this.getSearchBoxElement();
    if (!(searchBox instanceof HTMLInputElement)) {
      return;
    }

    if (popupVisible === true) {
      document.addEventListener("keydown", this.handleKeyDown);

      // The visible search field lives in the popup header (picker-popup-chrome);
      // antd's own input stays hidden, so the picker row keeps its layout.
      const opts = this.getEngine().generateItems(this.props.targetTypeName, this.props.targetCardinality);
      this.setState({ options: opts, popupVisible: true, popupSearchText: "" }, () => {
        this.setupPopupChrome();
        // Caret in the search field as soon as the picker opens, so you can type straight away.
        // Deferred by a frame because antd focuses its OWN hidden input while opening the popup,
        // which would undo a focus set in this commit; a frame also guarantees the portal holding
        // our field has mounted (on the first open the header only exists once
        // `popupChromeReady` renders). requestAnimationFrame rather than a timer: `worker-timers`
        // shadows setTimeout in this app. Same fix as the action picker's.
        requestAnimationFrame(this.focusPopupSearchInput);
      });
    } else {
      document.removeEventListener("keydown", this.handleKeyDown);
      this.clearPopupSearch();
      this.setState({ popupVisible: false });
    }
  };

  onSymbolPicked = (value: CascaderValueType, selectedOptions?: CascaderOptionType[]) => {
    const symbol = this.getEngine().buildSymbolFromSelectedOptions(value, selectedOptions);
    if (symbol) {
      this.props.onSymbolPicked?.(symbol);
    }
  };

  componentWillUnmount = () => {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.clearPopupSearch();
  };

  // The transparent `ant-cascader-picker-label` is what actually receives a click on this picker: it
  // is stretched over the row by the styling in componentDidMount, covering every sibling including
  // the `<base>SymbolPicker` wand. antd renders it, so it cannot carry a data-testid from JSX.
  //
  // Stamped from BOTH the mount and the update path — antd has not always rendered the label by
  // componentDidMount, and componentDidUpdate is what reaches it in that case (measured against a
  // local designer, 2026-09-01, on the sibling action picker: the label carried the styling but no
  // test-id while the stamp lived only in the mount path).
  stampTriggerTestId = (searchLabel: HTMLElement): void => {
    searchLabel.setAttribute("data-testid", `${this.getPickerNameBase()}SymbolPickerTrigger`);
  };

  /**
   * Make the label the transparent click target, and give it the width of the trigger it covers.
   *
   * A single-line picker's trigger is a 20px glyph (`.kcascader_single-line`), so the flat
   * `min-width: 100px` this carried put 80px of invisible overlay past it — over the next control
   * in the right panel, and past the body of the Add Condition dialog, where `.k-modal-body`'s
   * `overflow-y: auto` turns any x overflow into a scrollbar on both axes. A full-width picker
   * still gets the row-sized target it always had.
   *
   * Styling and stamp live together so the mount and the update path cannot style the label
   * without naming it; `picker-trigger-testids.test.ts` pins that nothing styles it outside here.
   */
  styleTriggerLabel = (searchLabel: HTMLElement): void => {
    searchLabel.style.minWidth = this.props.showInSingleLine === true ? "100%" : "100px";
    searchLabel.style.color = "#ffffff00";
    searchLabel.style.background = "#ffffff00";
    this.stampTriggerTestId(searchLabel);
  };

  /**
   * Hide antd's own input, turn its label into the transparent click target, and move the wand /
   * category trigger (`kuika_addNavigationLink<hash>`) inside antd's picker span so the label covers it.
   *
   * Idempotent, synchronous, and scoped to this picker's root: it runs from componentDidMount AND
   * from every componentDidUpdate. It used to be split — the mount path did it once, and the update
   * path re-did it inside a 300ms timer, gated on the ABSENCE of an element id that nothing in the
   * designer renders any more, so every single update of every picker on the
   * panel queued a timer that re-queried the document. Doing it inline on each update covers the
   * case the timer existed for (antd not having rendered the label yet at mount) without the delay.
   */
  decorateTrigger = (): void => {
    const root: HTMLDivElement | null = this._rootEl;
    // The columns popup's trigger is a plain button of our own; there is no antd label to dress.
    if (!root || this.isColumnsV2) return;

    const searchBox: HTMLElement | null = root.querySelector<HTMLElement>(".ant-cascader-input");
    if (searchBox) {
      searchBox.style.display = "none";
    }

    const searchLabel: HTMLElement | null = root.querySelector<HTMLElement>(".ant-cascader-picker-label");
    if (!searchLabel) return;
    this.styleTriggerLabel(searchLabel);
    searchLabel.replaceChildren("");

    const picker: HTMLElement | null = searchLabel.parentElement;
    if (!picker) return;
    const addLink: HTMLElement | null = root.querySelector<HTMLElement>(
      `.kuika_addNavigationLink${this.state.uniqueKey.toString().substring(0, 8)}`
    );
    if (addLink && addLink.parentElement !== picker && picker.classList.contains("ant-cascader-picker-show-search")) {
      picker.appendChild(addLink);
    }
    const closeCircle: HTMLElement | null = picker.querySelector<HTMLElement>(".anticon-close-circle");
    if (closeCircle) {
      closeCircle.style.display = "none";
    }
    const arrow: HTMLElement | null = picker.querySelector<HTMLElement>(".ant-cascader-picker-arrow");
    if (arrow) {
      arrow.style.display = "none";
    }
  };

  componentDidMount = (): void => {
    this.decorateTrigger();
  };

  componentDidUpdate = (prevProps: ISymbolPickerCascaderProps & IWithSDProps): void => {
    if (prevProps.sd.isDynamicTypesLoading === true && this.props.sd.isDynamicTypesLoading === false) {
      const options = _.cloneDeep(this.state.options);
      const actionResultsOption = options.find((x) => x.category === ESymbolCategory.actionResults);
      if (actionResultsOption && actionResultsOption.children) {
        const dynamicTypesLoadingOptionIndex = actionResultsOption.children.findIndex((x) => x.dynamicTypesOption);
        if (dynamicTypesLoadingOptionIndex !== -1) {
          actionResultsOption.children.splice(dynamicTypesLoadingOptionIndex, 1);
          this.setState({ options });
        }
      }
    }
    this.decorateTrigger();
  };

  _decoratedOptionsMemo: IDecoratedOptionsMemo | null = null;

  /**
   * The test-id-decorated copy of `state.options` (symbol-picker-option-testids.tsx: a label id and
   * a unique path id per row), rebuilt only when that array's identity changes
   * (a fresh `generateItems` on open, or the dynamic-types splice above).
   *
   * Rebuilding it per render was not just a wasted tree walk: antd's `getDerivedStateFromProps`
   * re-runs `flattenTree` over the whole option tree whenever the `options` prop changes identity,
   * so every render of an open picker re-flattened every path twice over.
   */
  getDecoratedOptions = (): CascaderOptionType[] => {
    const source: CascaderOptionType[] = this.state.options;
    const memo: IDecoratedOptionsMemo | null = this._decoratedOptionsMemo;
    if (memo && memo.source === source) {
      return memo.decorated;
    }
    const decorated: CascaderOptionType[] = decorateSymbolPickerOptions(source);
    this._decoratedOptionsMemo = { source, decorated };
    return decorated;
  };

  // Matching/ranking/rendering live in shared/utility/symbol-picker-search (KOA-18235). antd calls
  // this once per flattened path on every render while a search is running, so it must stay a pure
  // match: it used to also start a 300ms timer per call to clear a column width that antd only ever
  // writes when `matchInputWidth` is on — and this picker passes `false`.
  filter = (searchText: string, path: CascaderOptionType[]): boolean => matchSymbolPickerPath(searchText, path);

  _searchResultCountMemo: ISymbolSearchResultCountMemo | null = null;

  /**
   * "Search Results (n)" for the popup header, on the same terms as the action picker's — same
   * header component, same badge, and the same reason: a flat result list gives no sense of how
   * much matched, and this picker's paths are long enough that the list looks arbitrary without it.
   *
   * Counted with the picker's OWN matcher and cap, not the action picker's, so the badge can never
   * disagree with the rows antd renders. Memoised on (search text, options) so a re-render with the
   * popup open never repeats the scan — the options array is rebuilt from state on every render, so
   * identity is the right key.
   */
  getSearchResultCount = (): ICascaderSearchResultCount | null => {
    const searchText: string = this.state.popupSearchText;
    if (searchText === "") {
      return null;
    }
    const options: CascaderOptionType[] = this.state.options;
    const memo: ISymbolSearchResultCountMemo | null = this._searchResultCountMemo;
    if (memo && memo.searchText === searchText && memo.options === options) {
      return memo.count;
    }
    const count: ICascaderSearchResultCount = countCascaderSearchResults(
      options,
      (path: CascaderOptionType[]): boolean => matchSymbolPickerPath(searchText, path),
      SEARCH_MATCH_LIMIT
    );
    this._searchResultCountMemo = { searchText, options, count };
    return count;
  };

  // Base name for this picker's data-testids (`<base>SymbolPickerInput` on the cascader's
  // input, `<base>SymbolPicker` on the category trigger).
  //
  // `parameter` / `property` cover the generic action-parameter and component-property
  // paths. Symbol groups that render a picker without either — Notify's message, Confirmation's
  // header/message, Recurring's interval, and the notify custom-style properties — pass an
  // explicit `data_testid` instead; without it in this chain every one of those pickers
  // rendered the literal id `undefinedSymbolPicker*`, which is both unaddressable and shared
  // between them. Precedence keeps parameter/property first so ids already relied on by tests
  // are unchanged, and the result is `undefined` (attribute omitted) when no name is available.
  //
  // The `data_testid` fallback is skipped only when the wand icon actually renders: a single-line
  // picker without a custom `pickerIcon` already stamps `<data_testid>SymbolPicker` on that wand
  // inside the trigger, so reusing the same base here would emit a duplicate id. A single-line
  // picker that brings its own `pickerIcon` never reaches the wand branch — the condition modal's
  // Field / Value pickers are that shape, and the explicit id is the only handle they have.
  private getTestIdBase = (): string | undefined => {
    const rendersWandIcon: boolean = this.props.showInSingleLine === true && !this.props.pickerIcon;
    const explicit: string | undefined = rendersWandIcon ? undefined : this.props.data_testid;
    return this.props.parameter?.name ?? this.props.property?.name ?? explicit;
  };

  // The name used for this picker's own sub-elements — the trigger and the popup header's search
  // field. Same precedence as getTestIdBase, but WITHOUT the wand-icon exclusion: that exclusion
  // exists only because the wand already stamps `<data_testid>SymbolPicker`, and these two ids end
  // in `SymbolPickerTrigger` / `SymbolPickerPopupSearch`, so they cannot collide with it. Keeping
  // the exclusion here is what left single-line pickers (the generic-select filter's Value cell)
  // sharing one un-prefixed `symbolPicker*` name with every other nameless picker on the page.
  private getPickerNameBase = (): string => {
    return this.props.parameter?.name ?? this.props.property?.name ?? this.props.data_testid ?? "symbolPicker";
  };

  /**
   * The category name and the wand (or the host's own `pickerIcon`) — the visible face of the trigger,
   * shared by the Cascader and the columns popup. `<base>SymbolPicker` is the handle tests click.
   */
  renderCategoryTrigger = (): JSX.Element => {
    const { showInSingleLine } = this.props;
    const testIdBase: string | undefined = this.getTestIdBase();
    return (
      <span
        data-testid={testIdBase ? `${testIdBase}SymbolPicker` : undefined}
        className={`kuika_addNavigationLink kuika_addNavigationLink${this.state.uniqueKey.toString().substring(0, 8)}`}
        style={{ margin: 0, paddingBottom: 4 }}
      >
        <div
          style={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
            pointerEvents: "none"
          }}
        >
          {showInSingleLine ? (
            <>
              {this.props.pickerIcon || <KuikaSvg data-testid={`${this.props.data_testid}SymbolPicker`} name="wand" />}
            </>
          ) : (
            <>
              <label
                className="k_Regular-G1-Left kuika_rightPanelDIName"
                style={{ fontWeight: 400, textTransform: "capitalize" }}
              >
                {this.props.categoryName === "screenInput" &&
                this.props.sd.dashboardState === DashboardState.reportDesigner
                  ? "Report Input"
                  : this.props.categoryName}
              </label>
              <>
                {this.props.pickerIcon || (
                  <svg id="Wand" width="16" height="16" viewBox="0 0 24 24">
                    <path d="M14.308 4.864l-.002.003-.786 2.072-1.99-1.01-.003-.002c-.162-.081-.36-.016-.44.147-.045.089-.047.193-.005.283l.928 2.02-2.132.705-.006.001c-.17.056-.263.238-.207.409.03.093.1.167.192.202l1.796.694-2.173 2.194c-.025.024-.045.05-.062.081l-5.091 5.082-.01.01c-.404.448-.413 1.14.01 1.561l.367.368c.448.403 1.139.412 1.561-.01l5.163-5.154.52-.53 1.562-1.561.571 1.704c.058.17.242.261.412.204.091-.031.164-.1.2-.19v-.004l.817-2.082 1.97.98c.161.08.358.015.439-.147.043-.087.046-.19.006-.279l-.895-2.013 2.102-.673.006-.002c.17-.056.262-.24.206-.41-.03-.092-.1-.166-.191-.2L17.06 8.5l1.01-1.97c.083-.162.018-.36-.145-.442-.088-.045-.192-.047-.282-.007h-.001l-2.02.909-.705-2.112c-.056-.17-.239-.263-.409-.207-.093.03-.167.101-.201.193zm.284 1.085l.53 1.582.002.003c.054.168.233.26.4.205.013-.004.025-.009.037-.015l1.51-.663-.755 1.46c-.081.157-.02.351.139.433.01.005.02.01.03.013l.005.002 1.561.613-1.582.51-.003.001c-.168.054-.26.233-.205.4.004.013.009.026.015.038l.673 1.52-1.475-.738c-.158-.079-.35-.015-.429.143l-.014.034-.623 1.581-.388-1.163 1.051-1.05c.423-.423.414-1.114.01-1.562l-.02-.02-.377-.347-.01-.01c-.224-.217-.512-.317-.776-.317-.268 0-.561.102-.786.327h-.01l-.939.949-1.214-.47 1.612-.53.004-.002c.167-.054.259-.233.205-.4-.004-.013-.01-.025-.015-.037l-.694-1.51 1.49.755.006.003c.158.079.35.015.429-.143L14 7.51l.592-1.561zm-.694 3.306c.124 0 .214.03.316.133l.01.01.378.337c.185.205.176.497.01.663l-2.908 2.898-1-1 2.867-2.908c.102-.102.203-.133.327-.133zm-3.653 3.5l1 1-.296.296c-.015.015-.03.033-.04.051l-5.113 5.112c-.163.164-.448.175-.653 0l-.01-.01-.296-.306-.051-.04c-.164-.164-.175-.449 0-.654l5.102-5.102.03-.02.327-.327zm6.584 2.691l-.074.187-.408 1.04-1.04.409-.187.073c-.129.05-.192.196-.141.325.025.064.076.115.14.14l.187.074 1.04.408.41 1.04.073.187c.05.129.196.192.324.141.064-.025.115-.076.14-.14l.074-.187.409-1.041 1.04-.408.187-.074c.128-.05.191-.196.14-.324-.025-.064-.076-.115-.14-.14l-.187-.074-1.04-.409-.409-1.04-.073-.187c-.05-.128-.196-.191-.325-.14-.064.025-.115.076-.14.14zm-3.951-.344l-.327.816-.816.327.816.326.327.817.326-.817.816-.326-.816-.327-.326-.816zm4.183 1.561l.153.388.051.133.133.05.388.154-.388.153-.133.05-.05.133-.154.388-.153-.388-.05-.132-.134-.051-.387-.153.387-.153.133-.051.051-.133.153-.388z"></path>
                  </svg>
                )}
              </>
            </>
          )}
        </div>
      </span>
    );
  };

  /** The preview pane's host facts for the open popup — built on open, like the tree (see below). */
  _previewContext: ISymbolPickerPreviewContext | undefined = undefined;

  /** The columns popup opening or closing (`symbolPickerColumnsV2`). The tree is built on open, as the Cascader's is. */
  handleColumnsOpenChange = (open: boolean): void => {
    if (open) {
      const engine: DesignerSymbolPickerEngine = this.getEngine();
      const options: CascaderOptionType[] = engine.generateItems(
        this.props.targetTypeName,
        this.props.targetCardinality
      );
      this._previewContext = buildSymbolPickerPreviewContext(this.props, {
        isRepeatingParent: (di: IDisplayItem): boolean => engine.isDataBindingOptions(di),
        getComponentLabel: (di: IDisplayItem): string => engine.getBackwardsCompatibleDiItemLabel(di)
      });
      this.setState({ options, popupVisible: true });
      return;
    }
    this.setState({ popupVisible: false });
  };

  /**
   * The picker on the columns popup. Same trigger face (category name + wand), same ids — the trigger
   * button is `<name>SymbolPickerTrigger` inside `<base>SymbolPickerInput`, the wand keeps
   * `<base>SymbolPicker`, the search field is `<name>SymbolPickerPopupSearch`, the rows carry the
   * decorator's `symbolPickerOption-*` / `symbolPickerOptionPath-*` — and the same `onSymbolPicked`
   * receives what the Cascader's `onChange` did.
   */
  renderColumnsPicker = (): JSX.Element => {
    const { showInSingleLine } = this.props;
    const testIdBase: string | undefined = this.getTestIdBase();
    const nameBase: string = this.getPickerNameBase();
    return (
      <>
        <div
          ref={this.saveRootElement}
          className={classNames("kcascader_root", "kuika-symbol-picker-trigger-root", {
            "kcascader_single-line": showInSingleLine
          })}
        >
          {showInSingleLine && <div className="singleline-symbolpicker-children-container">{this.props.children}</div>}
          <span
            className={classNames(
              "kuika-symbol-picker-trigger",
              this.props.data_testid ? `kcascader-trigger-${this.props.data_testid}` : undefined
            )}
            data-testid={testIdBase ? `${testIdBase}SymbolPickerInput` : undefined}
          >
            <SymbolPickerPopup
              options={this.getDecoratedOptions()}
              open={this.state.popupVisible && this.props.disabled !== true}
              onOpenChange={this.handleColumnsOpenChange}
              onChange={this.onSymbolPicked}
              testIdBase={`${nameBase}SymbolPicker`}
              className={this.props.data_testid ? `kcascader-popup-${this.props.data_testid}` : undefined}
              popupPlacement={this.props.popupPlacement}
              midScreen={this.props.stablePopup === true}
              previewContext={this._previewContext}
            >
              <button
                type="button"
                className="kuika-symbol-picker-trigger__button"
                data-testid={`${nameBase}SymbolPickerTrigger`}
                disabled={this.props.disabled}
                // Only the single-line trigger is a bare glyph; the full one reads its category name.
                aria-label={showInSingleLine ? "Choose what this value binds to" : undefined}
              >
                {this.renderCategoryTrigger()}
              </button>
            </SymbolPickerPopup>
          </span>
        </div>
        {!showInSingleLine && this.props.children}
      </>
    );
  };

  render() {
    if (this.isColumnsV2) {
      return this.renderColumnsPicker();
    }
    const { showInSingleLine } = this.props;
    const testIdBase: string | undefined = this.getTestIdBase();

    // Stable, per-picker handles on antd's own trigger and dropdown elements, for tests that must
    // address ONE picker among several on screen.
    //
    // The dropdown is the reason this exists: antd portals it to <body>, keeps it mounted after it
    // closes (`destroyPopupOnHide` is off) and deliberately caches the last rendered options while
    // closed, so several pickers' popups — each still holding its own search results — coexist in
    // the DOM. Without a way to say "this picker's popup", an option lookup by label can hit a
    // stale one (QA-411: Field=True then Value=True matched two "Default / True" rows). The
    // `kcascader_<hash>` class below cannot serve — it is regenerated on every mount.
    //
    // Classes rather than test-ids because both elements are antd internals we do not render, and
    // the trigger's own children (the transparent `ant-cascader-picker-label`, sized in
    // componentDidMount) cover the row and swallow clicks aimed at anything inside it. Derived
    // from the explicit `data_testid` only, so this is an opt-in contract rather than a name
    // accidentally built from a parameter or property label.
    const triggerTestIdClass: string | undefined = this.props.data_testid
      ? `kcascader-trigger-${this.props.data_testid}`
      : undefined;
    const popupTestIdClass: string | undefined = this.props.data_testid
      ? `kcascader-popup-${this.props.data_testid}`
      : undefined;

    const styles: CSSProperties = {
      display: "flow-root",
      width: "100%"
    };

    if (showInSingleLine) {
      styles.display = "flex";
      styles.width = "100%";
    }

    return (
      <>
        <div
          ref={this.saveRootElement}
          style={styles}
          className={classNames(`kcascader_root_${this.state.uniqueKey.toString().substring(0, 8)}`, "kcascader_root", {
            "kcascader_single-line": showInSingleLine
          })}
        >
          {showInSingleLine && <div className="singleline-symbolpicker-children-container">{this.props.children}</div>}
          <Cascader
            notFoundContent={<PickerPopupEmptyState />}
            className={triggerTestIdClass}
            showSearch={{
              // The visible search field is in the popup header; antd's own input is
              // hidden, and matching its 0 offsetWidth would collapse the result list.
              matchInputWidth: false,
              filter: this.filter,
              sort: compareSymbolPickerPaths,
              render: renderSymbolPickerSearchRow,
              limit: SEARCH_MATCH_LIMIT
            }}
            options={this.getDecoratedOptions()}
            popupVisible={this.state.popupVisible}
            popupClassName={classNames(
              { "sticky-symbol-picker": !showInSingleLine },
              // Opt-in, not derived from showInSingleLine: the condition and query-builder pickers are single-line
              // too, but they sit in panels where opening leftwards is correct. Only a caller with room to its right
              // — a canvas — asks for this.
              { "stable-symbol-picker": this.props.stablePopup === true },
              "reverseCascade",
              "cascaderActionPicker",
              `kcascader_${this.state.uniqueKey.toString().substring(0, 8)}`,
              // Search state lives on popupClassName: antd rewrites the popup's
              // className on every re-render, so DOM-toggled classes get wiped.
              { "kuika-action-picker--searching": this.state.popupSearchText !== "" },
              popupTestIdClass
            )}
            data-testid={testIdBase ? `${testIdBase}SymbolPickerInput` : undefined}
            placeholder="Search.."
            expandTrigger="hover"
            expandIcon={PICKER_EXPAND_ICON}
            onPopupVisibleChange={this.getOptions}
            onChange={this.onSymbolPicked}
            disabled={this.props.disabled}
            popupPlacement={this.props.popupPlacement}
          />

          {this.state.popupVisible && this.state.popupChromeReady && this._popupHeaderEl
            ? ReactDOM.createPortal(
                <PickerPopupHeader
                  searchText={this.state.popupSearchText}
                  searchPlaceholder="Search.."
                  testIdBase={`${this.getPickerNameBase()}SymbolPicker`}
                  resultCount={this.getSearchResultCount()}
                  onSearchChange={this.handlePopupSearchChange}
                  onSearchKeyDown={this.handlePopupSearchKeyDown}
                />,
                this._popupHeaderEl
              )
            : null}
          {this.renderCategoryTrigger()}
        </div>
        {!showInSingleLine && this.props.children}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.UpdateVisibilty ||
      changeReason === ESdChangeReason.ChangePropertyType ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const symbolPickerCascader = withSD<ISymbolPickerCascaderProps>(SymbolPickerCascader);
export { symbolPickerCascader as SymbolPickerCascader };
